import { getGeocode, getPlaceDetails, getSuggestions } from 'api/location';
import type { GetPlaceDetailsResponse } from 'api/location/types';
import getDistanceForMobilityApi from 'api/mobilityContinuation/getDistanceForMobility';
import { push } from 'connected-react-router';
import { call, put, select } from 'redux-saga/effects';
import spinnerActions from 'redux/actions/applicationSpinnerActions';
import actions from 'redux/actions/mobilityContinuationActions';
import { caseDataCreatedSelector } from 'redux/selectors/caseCreation';
import { caseReferenceSelector } from 'redux/selectors/caseStatus';
import {
  currentLanguageSelector,
  mapValidCountriesSelector,
  selectMoveVersion,
  selectTenantId,
  computableContractsStaticQuestionsSelector,
  computableContractsDefaultDynamicQuestionsSelector,
  partnerSelector,
} from 'redux/selectors/config';
import {
  mobilityContinuationSelectedTemporalCoordsSelector,
  mobilityContinuationConsentSelector,
} from 'redux/selectors/mobilityContinuation';
import { routes } from 'Routes/routesConfig';
import {
  ApiResponse,
  hasDataProperty,
} from 'ts/interfaces/api-response.interface';
import type { CaseCreationData } from 'ts/types/case-creation.types';
import { GoogleSuggestionsApiResponse } from 'ts/types/location.types';
import type {
  GetLocationByCoordinatesPayload,
  GetSuggestionsPayload,
} from 'ts/types/mobility-continuation.types';
import type { ComputableContractsQuestions } from 'ts/types/coverage-questions.types';
import {
  isBingMapCoordinate,
  parseCoordinatesToBing,
  parseCountryCodes,
} from 'utils/map-utils';
import { getSynergySessionToken } from 'utils/sagas-utils';
import { generateCoverageResponse } from './utils';

export function* getLocationByCoordinates(action: {
  type: string;
  payload: GetLocationByCoordinatesPayload;
}) {
  try {
    const { coords, type } = action.payload;

    if (!coords || isBingMapCoordinate(coords)) {
      return;
    }

    const language: string = yield select(currentLanguageSelector);
    const response: ApiResponse<any> = yield call(getGeocode, {
      latitude: coords.lat,
      longitude: coords.lng,
      language,
    });

    yield put(
      actions.setMobilityContinuationUpdateTemporalLocation({
        type,
        value: {
          coordinates: coords,
          address: response.result,
        },
      }),
    );
  } catch (error) {
    console.log('Error while getting location by coordinates', error);
  }
}

export function* getLocationByQuery(action: {
  type: string;
  payload: GetSuggestionsPayload;
}) {
  const { input, type } = action.payload;

  if (!input) {
    if (type === 'start') {
      yield put(actions.setMobilityContinuationQueryResultsForStart([]));
    }
    if (type === 'destination') {
      yield put(actions.setMobilityContinuationQueryResultsForDestination([]));
    }
    return;
  }

  try {
    const language: string = yield select(currentLanguageSelector);
    const countryCodes: string[] | undefined = yield select(
      mapValidCountriesSelector,
    );
    if (!countryCodes) {
      throw new Error('No country code set, check Prismic configuration');
    }
    const components = parseCountryCodes(countryCodes);
    const addressResponse: ApiResponse<GoogleSuggestionsApiResponse> =
      yield call(getSuggestions, {
        input,
        language,
        components,
      });

    if (addressResponse.status !== 200) {
      throw new Error('Error while getting suggestions');
    }

    if (type === 'start') {
      yield put(
        actions.setMobilityContinuationQueryResultsForStart(
          addressResponse.result,
        ),
      );
    }

    if (type === 'destination') {
      yield put(
        actions.setMobilityContinuationQueryResultsForDestination(
          addressResponse.result,
        ),
      );
    }
  } catch (error) {
    console.error(error);
    yield put(actions.setMobilityContinuationQueryError(type));
  }
}

export function* getLocationByPlaceId(action: any) {
  try {
    const { type, value } = action.payload;

    if (!value.placeId) {
      throw new Error('placeId is missing in payload');
    }
    const language: string = yield select(currentLanguageSelector);
    const response: ApiResponse<GetPlaceDetailsResponse> = yield call(
      getPlaceDetails,
      {
        language,
        placeId: value.placeId,
      },
    );

    let payload;
    if (hasDataProperty(response.result)) {
      payload = response.result.data;
    } else {
      payload = response.result;
    }

    yield put(
      actions.setMobilityContinuationUpdateTemporalLocation({
        type,
        value: {
          address: payload.address,
          coordinates: payload.location,
        },
      }),
    );
  } catch (error) {
    console.error('Error while fetching location by place id', error);
  }
}

export function* getProductIdAndDistanceSaga() {
  try {
    yield put(spinnerActions.startLoad({}));
    const caseData: CaseCreationData = yield select(caseDataCreatedSelector);
    const caseReference: string = yield select(caseReferenceSelector);
    const partner: string = yield select(partnerSelector);
    const token: string = yield getSynergySessionToken();
    const tenantId: string = yield select(selectTenantId);
    const moveVersion: string = yield select(selectMoveVersion);
    const coordinates: ReturnType<
      typeof mobilityContinuationSelectedTemporalCoordsSelector
    > = yield select(mobilityContinuationSelectedTemporalCoordsSelector);

    const staticQuestions: ComputableContractsQuestions[] = yield select(
      computableContractsStaticQuestionsSelector,
    );
    const defaultDynamicQuestions: ComputableContractsQuestions[] =
      yield select(computableContractsDefaultDynamicQuestionsSelector);

    if (!coordinates.origin || !coordinates.destination) {
      throw new Error('One of the coordinates were not defined');
    }

    const parsedDestinationCoords = parseCoordinatesToBing(
      coordinates.destination,
    );
    const parsedOriginCoords = parseCoordinatesToBing(coordinates.origin);

    const { result } = yield call(getDistanceForMobilityApi, {
      body: {
        caseId: caseReference,
        destinationAddress: parsedDestinationCoords,
        startAddress: parsedOriginCoords,
      },
      moveVersion,
      partner,
      tenantId,
      token,
    });

    const currentTime = new Date();
    const consent: ReturnType<typeof mobilityContinuationConsentSelector> =
      yield select(mobilityContinuationConsentSelector);

    let payload;
    if (hasDataProperty(result)) {
      payload = result.data;
    } else {
      payload = result;
    }

    yield put(actions.setProductId(payload.productId));
    const response: any = generateCoverageResponse({
      caseData,
      consent,
      currentTime,
      defaultDynamicQuestions,
      payload,
      staticQuestions,
    });
    yield put(actions.setCoverageResponses(response));
    yield put(push(routes.SYNERGY_COVERAGE_QUESTIONS));
  } catch (error) {
    console.error(error);
    yield put(push(routes.DROPOFF));
  } finally {
    yield put(spinnerActions.stopLoad({}));
  }
}
